import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { api } from '@/lib/api';

interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer' | 'user';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = 'market_commentary_token';
const REFRESH_TOKEN_KEY = 'market_commentary_refresh_token';

// Token expiry buffer - refresh 1 minute before expiry
const EXPIRY_BUFFER_MS = 60 * 1000;

// Parse JWT to get expiration time
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  });
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any scheduled refresh
  const clearRefreshTimeout = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  // Clear tokens and user state (internal helper)
  const clearAuth = useCallback(() => {
    clearRefreshTimeout();
    setUser(null);
    setToken(null);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }, [clearRefreshTimeout]);

  // Logout function
  const logout = useCallback(async () => {
    // Try to revoke refresh token on server
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken });
      } catch {
        // Ignore errors on logout
      }
    }
    clearAuth();
  }, [clearAuth]);

  // Refresh tokens and schedule next refresh
  const doTokenRefresh = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      clearAuth();
      return false;
    }

    try {
      const response = await api.post<{
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
      }>('/auth/refresh', { refreshToken });

      if (response.success && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        setToken(accessToken);
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

        // Schedule next refresh
        const expiry = getTokenExpiry(accessToken);
        if (expiry) {
          const timeUntilRefresh = expiry - Date.now() - EXPIRY_BUFFER_MS;
          if (timeUntilRefresh > 0) {
            clearRefreshTimeout();
            refreshTimeoutRef.current = setTimeout(() => {
              doTokenRefresh();
            }, timeUntilRefresh);
          }
        }
        return true;
      } else {
        clearAuth();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearAuth();
      return false;
    }
  }, [clearAuth, clearRefreshTimeout]);

  // Schedule token refresh for the current token
  const scheduleTokenRefresh = useCallback((accessToken: string) => {
    const expiry = getTokenExpiry(accessToken);
    if (!expiry) return;

    const timeUntilRefresh = expiry - Date.now() - EXPIRY_BUFFER_MS;

    clearRefreshTimeout();

    if (timeUntilRefresh <= 0) {
      // Token already expired or about to expire, refresh now
      doTokenRefresh();
      return;
    }

    refreshTimeoutRef.current = setTimeout(() => {
      doTokenRefresh();
    }, timeUntilRefresh);
  }, [clearRefreshTimeout, doTokenRefresh]);

  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!currentToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get<User>('/auth/me');
      if (response.success && response.data) {
        setUser(response.data);
        scheduleTokenRefresh(currentToken);
      } else {
        // Try to refresh token
        const refreshed = await doTokenRefresh();
        if (refreshed) {
          // Retry getting user
          const retryResponse = await api.get<User>('/auth/me');
          if (retryResponse.success && retryResponse.data) {
            setUser(retryResponse.data);
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      await doTokenRefresh();
    } finally {
      setIsLoading(false);
    }
  }, [scheduleTokenRefresh, doTokenRefresh]);

  useEffect(() => {
    refreshUser();

    return () => {
      clearRefreshTimeout();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.post<{
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      token: string; // Legacy field
      user: User;
    }>('/auth/login', { email, password });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Login failed');
    }

    const { accessToken, refreshToken, user: userData } = response.data;

    // Use accessToken if available, fall back to legacy token field
    const newToken = accessToken || response.data.token;

    setToken(newToken);
    setUser(userData);
    localStorage.setItem(ACCESS_TOKEN_KEY, newToken);

    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      scheduleTokenRefresh(newToken);
    }
  };

  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.role === 'admin';
  const isEditor = user?.role === 'admin' || user?.role === 'editor';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated,
        isAdmin,
        isEditor,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
